import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { DriverPayment, MonthlyDeductionRecord, PaymentSummary, Invoice, DeductionItem } from '../../config/types';
import { format } from 'date-fns';

interface PaymentState {
  driverPayments: DriverPayment[];
  monthlyDeductions: MonthlyDeductionRecord[];
  paymentSummaries: PaymentSummary[];
  loading: boolean;
  error: string | null;
}

const initialState: PaymentState = {
  driverPayments: [],
  monthlyDeductions: [],
  paymentSummaries: [],
  loading: false,
  error: null
};

// Create a payment record for paid invoices
export const createPaymentRecord = createAsyncThunk(
  'payments/createPaymentRecord',
  async ({ 
    driverId, 
    invoiceIds,
    notes 
  }: { 
    driverId: string;
    invoiceIds: string[];
    notes?: string;
  }) => {
    try {
      // Get driver info for percentage cut and monthly deductions
      const driverDoc = await getDocs(query(collection(db, 'users'), where('dogtag', '==', driverId)));
      const driverData = driverDoc.docs[0].data();
      const percentageCut = driverData.percentageCut || 0;
      const monthlyDeductions = driverData.monthlyDeductions || [];

      // Get invoices
      const invoicesQuery = query(
        collection(db, 'invoices'),
        where('id', 'in', invoiceIds)
      );
      const invoicesDocs = await getDocs(invoicesQuery);
      const invoices = invoicesDocs.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Invoice[];

      // Calculate gross amount
      const grossAmount = invoices.reduce((total, invoice) => total + (invoice.totalCharges || 0), 0);

      // Check if monthly deductions were already processed this month
      const currentMonth = format(new Date(), 'yyyy-MM');
      const monthlyDeductionsQuery = query(
        collection(db, 'monthlyDeductions'),
        where('driverId', '==', driverId),
        where('month', '==', currentMonth)
      );
      const monthlyDeductionsDoc = await getDocs(monthlyDeductionsQuery);
      const monthlyDeductionsProcessed = !monthlyDeductionsDoc.empty;

      // Calculate deductions
      const percentageDeduction = grossAmount * (percentageCut / 100);
      const monthlyDeductionsTotal = monthlyDeductionsProcessed ? 0 : 
        monthlyDeductions.reduce((total: number, deduction: DeductionItem) => total + deduction.amount, 0);
      const totalDeductions = percentageDeduction + monthlyDeductionsTotal;

      // Create payment record
      const payment: Omit<DriverPayment, 'id'> = {
        driverId,
        invoiceIds,
        grossAmount,
        deductions: {
          percentageCut,
          monthlyDeductions: monthlyDeductionsProcessed ? [] : monthlyDeductions,
          totalDeductions
        },
        netAmount: grossAmount - totalDeductions,
        status: 'pending',
        createdAt: new Date(),
        notes
      };

      // Save to Firestore
      const paymentRef = await addDoc(collection(db, 'driverPayments'), {
        ...payment,
        createdAt: Timestamp.fromDate(payment.createdAt)
      });

      // If monthly deductions were applied, create record
      if (!monthlyDeductionsProcessed && monthlyDeductions.length > 0) {
        await addDoc(collection(db, 'monthlyDeductions'), {
          driverId,
          month: currentMonth,
          deductions: monthlyDeductions,
          processed: true,
          processedAt: Timestamp.fromDate(new Date())
        });
      }

      // Update invoice statuses
      for (const invoiceId of invoiceIds) {
        await updateDoc(doc(db, 'invoices', invoiceId), {
          status: 'paid_to_driver',
          updatedAt: Timestamp.fromDate(new Date())
        });
      }

      return { id: paymentRef.id, ...payment };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create payment record');
    }
  }
);

// Fetch payment records for a driver
export const fetchDriverPayments = createAsyncThunk(
  'payments/fetchDriverPayments',
  async (driverId: string) => {
    try {
      const paymentsQuery = query(
        collection(db, 'driverPayments'),
        where('driverId', '==', driverId),
        orderBy('createdAt', 'desc')
      );
      const paymentsDocs = await getDocs(paymentsQuery);
      
      if (paymentsDocs.empty) {
        return [];
      }

      return paymentsDocs.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        processedAt: doc.data().processedAt?.toDate(),
        paidAt: doc.data().paidAt?.toDate()
      })) as DriverPayment[];
    } catch (error: any) {
      if (error.code === 'permission-denied' || error.code === 'not-found') {
        return [];
      }
      throw new Error(error.message || 'Failed to fetch payment records');
    }
  }
);

// Get payment summary for a driver
export const fetchPaymentSummary = createAsyncThunk(
  'payments/fetchPaymentSummary',
  async ({ driverId, month }: { driverId: string; month: string }) => {
    try {
      const paymentsQuery = query(
        collection(db, 'driverPayments'),
        where('driverId', '==', driverId),
        where('createdAt', '>=', new Date(month + '-01')),
        where('createdAt', '<', new Date(month + '-31'))
      );
      const paymentsDocs = await getDocs(paymentsQuery);
      
      // If no documents exist, return empty summary
      if (paymentsDocs.empty) {
        return {
          driverId,
          month,
          totalGrossAmount: 0,
          totalDeductions: 0,
          totalNetAmount: 0,
          invoiceCount: 0,
          lastPaymentDate: undefined
        } as PaymentSummary;
      }

      const payments = paymentsDocs.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DriverPayment[];

      const summary: PaymentSummary = {
        driverId,
        month,
        totalGrossAmount: payments.reduce((total, payment) => total + payment.grossAmount, 0),
        totalDeductions: payments.reduce((total, payment) => total + payment.deductions.totalDeductions, 0),
        totalNetAmount: payments.reduce((total, payment) => total + payment.netAmount, 0),
        invoiceCount: payments.reduce((total, payment) => total + payment.invoiceIds.length, 0),
        lastPaymentDate: payments[0]?.createdAt
      };

      return summary;
    } catch (error: any) {
      // If collection doesn't exist, return empty summary instead of throwing error
      if (error.code === 'permission-denied' || error.code === 'not-found') {
        return {
          driverId,
          month,
          totalGrossAmount: 0,
          totalDeductions: 0,
          totalNetAmount: 0,
          invoiceCount: 0,
          lastPaymentDate: undefined
        } as PaymentSummary;
      }
      throw new Error(error.message || 'Failed to fetch payment summary');
    }
  }
);

const paymentSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Create payment record
      .addCase(createPaymentRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaymentRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.driverPayments.unshift(action.payload);
        state.error = null;
      })
      .addCase(createPaymentRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create payment record';
      })
      // Fetch driver payments
      .addCase(fetchDriverPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDriverPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.driverPayments = action.payload;
        state.error = null;
      })
      .addCase(fetchDriverPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch payment records';
      })
      // Fetch payment summary
      .addCase(fetchPaymentSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentSummaries = [
          ...state.paymentSummaries.filter(s => s.driverId !== action.payload.driverId || s.month !== action.payload.month),
          action.payload
        ];
        state.error = null;
      })
      .addCase(fetchPaymentSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch payment summary';
      });
  }
});

export const { clearError } = paymentSlice.actions;
export default paymentSlice.reducer; 